import el from "@cypherpotato/el";
import ApexCharts from "apexcharts";
import { themePreference } from "../global-state";
import { getUniqueColor } from "../ui/unique-color";

export function AreaChart({ series, xaxis, yaxis, colors, height = 300, sparkline = false, maxSeries = 6 }) {

    return el.state.listening([themePreference], () =>
        el(".chart",
            {
                mount() {
                    let processedSeries = series;

                    if (series && series.length > maxSeries) {
                        const sorted = [...series].sort((a, b) => {
                            const sumA = a.data.reduce((acc, d) => acc + (d.y || d[1] || 0), 0);
                            const sumB = b.data.reduce((acc, d) => acc + (d.y || d[1] || 0), 0);
                            return sumB - sumA;
                        });

                        const top = sorted.slice(0, maxSeries);
                        const rest = sorted.slice(maxSeries);

                        if (rest.length > 0) {
                            const othersData = {};
                            rest.forEach(s => {
                                s.data.forEach(d => {
                                    const x = d.x || d[0];
                                    const y = d.y || d[1] || 0;
                                    othersData[x] = (othersData[x] || 0) + y;
                                });
                            });

                            const othersSeries = {
                                name: "Others",
                                data: Object.entries(othersData).map(([x, y]) => ({ x: parseInt(x) || x, y }))
                            };

                            processedSeries = [...top, othersSeries];
                        } else {
                            processedSeries = top;
                        }
                    }

                    const chartOptions = {
                        series: processedSeries,
                        chart: {
                            type: "area",
                            height,
                            zoom: {
                                autoScaleYaxis: true
                            },
                            toolbar: {
                                show: false,
                            },
                            sparkline: {
                                enabled: sparkline
                            },
                            animations: {
                                enabled: true,
                                speed: 300,
                                animateGradually: {
                                    enabled: true,
                                    delay: 50
                                },
                                dynamicAnimation: {
                                    enabled: true,
                                    speed: 350
                                }
                            }
                        },
                        tooltip: {
                            theme: themePreference.value
                        },
                        dataLabels: {
                            enabled: false
                        },
                        legend: {
                            show: false
                        },
                        grid: {
                            borderColor: "var(--border-color)"
                        },
                        xaxis,
                        yaxis,
                        colors: colors || processedSeries.map(s => getUniqueColor(s.name + s.name.length)),
                        stroke: {
                            show: true,
                            width: 2
                        }
                    }
                    const chart = new ApexCharts(this, chartOptions);
                    chart.render();
                }
            }
        )
    );
}

export function PieChart({ series, labels, colors, height = 300 }) {

    return el.state.listening([themePreference], () =>
        el(".chart",
            {
                mount() {
                    const chartOptions = {
                        series,
                        labels,
                        chart: {
                            type: "donut",
                            height,
                            toolbar: {
                                show: false,
                            },
                            animations: {
                                enabled: true,
                                speed: 300,
                                animateGradually: {
                                    enabled: true,
                                    delay: 50
                                },
                                dynamicAnimation: {
                                    enabled: true,
                                    speed: 350
                                }
                            }
                        },
                        tooltip: {
                            theme: themePreference.value,
                            y: {
                                formatter: function (val) {
                                    return "$" + parseFloat(val).toFixed(4);
                                }
                            }
                        },
                        dataLabels: {
                            enabled: false
                        },
                        legend: {
                            show: false
                        },
                        colors: colors || labels.map((l, i) => getUniqueColor(l + i)),
                        stroke: {
                            show: true,
                            width: 2,
                            colors: [themePreference.value === "dark" ? "#1a1a1a" : "#fff"]
                        },
                        plotOptions: {
                            pie: {
                                donut: {
                                    size: "65%",
                                    labels: {
                                        show: true,
                                        name: {
                                            show: true,
                                            color: themePreference.value === "dark" ? "#fff" : "#333"
                                        },
                                        value: {
                                            show: true,
                                            color: themePreference.value === "dark" ? "#fff" : "#333",
                                            formatter: function (val) {
                                                return "$" + parseFloat(val).toFixed(2);
                                            }
                                        },
                                        total: {
                                            show: true,
                                            label: "Total",
                                            color: themePreference.value === "dark" ? "#fff" : "#333",
                                            formatter: function (w) {
                                                return "$" + w.globals.seriesTotals.reduce((a, b) => a + b, 0).toFixed(2);
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    const chart = new ApexCharts(this, chartOptions);
                    chart.render();
                }
            }
        )
    );
}

export function BarChart({ series, categories, colors, height = 300 }) {
    return el.state.listening([themePreference], () =>
        el(".chart",
            {
                mount() {
                    const chartOptions = {
                        series,
                        chart: {
                            type: "bar",
                            height,
                            toolbar: { show: false },
                            animations: { enabled: true, speed: 300 }
                        },
                        plotOptions: {
                            bar: {
                                horizontal: true,
                                barHeight: "70%"
                            }
                        },
                        dataLabels: { enabled: false },
                        legend: { show: false },
                        grid: { borderColor: "var(--border-color)" },
                        tooltip: {
                            theme: themePreference.value,
                            y: { formatter: val => "$" + parseFloat(val).toFixed(4) }
                        },
                        xaxis: {
                            categories,
                            labels: { show: false }
                        },
                        yaxis: {
                            labels: {
                                style: { fontSize: "11px" }
                            }
                        },
                        colors: colors || categories.map((c, i) => getUniqueColor(c + i))
                    };
                    const chart = new ApexCharts(this, chartOptions);
                    chart.render();
                }
            }
        )
    );
}